import { colorsHex } from "./colors"

const Unit = {
    Wattage: "W",
    Voltage: "V",
    LiterPerMinute: "L/min",
    Celsius: "ºC",
    Percent: "%",
}

const ArrowIcon = {
    Up: "up",
    Down: "down",
}

/**
 * Returns the rounded number as a string (to ensure the trailing zeroes are visible)
 *
 * @param {number} number
 * @param {number} decimals
 * @param {boolean} showTrailingZeroes
 * @returns {string}
 */
function roundNumber(number, decimals = 0, showTrailingZeroes = true) {
    const factor = Math.pow(10, decimals)
    let result = `${Math.round(number * factor) / factor}`

    if (showTrailingZeroes && decimals > 0) {
        const split = `${result}`.split('.')
        let remainder = split[1] ?? "0"

        for (let index = 0; index < decimals - remainder.length; index++) {
            remainder = remainder + '0'
        }

        let end = split[0] + '.' + remainder

        result = end
    }

    return result
}

/**
 * Convert graph data points to a data structure to push to app clients.
 *
 * @param {[{t: Date, y: number, live: boolean;}]} dataPoints
 * @param {string} type The GraphType
 * @returns {{value:string, unit:string, arrow:string, color:string}}
 */
function transformLiveDataPoints(dataPoints, type) {

    let data = []

    /**
     * Safeguards fetching a border property from the given map.
     *
     * @param {{l1:{border}, l2: {border}, l3: {border}}} colorMap
     * @param {number} line
     * @param {string} defaultColor
     * @returns {string}
     */
    const getLineBorderColor = (colorMap, line, defaultColor) => {
        const result = colorMap[`l${line}`]
        return result !== undefined ? result.border : defaultColor
    }

    switch (type) {
        case 'overvoltage': {
            // When multiple phases are to be displayed we need to use a different color for the unit
            const multiplePhases = dataPoints.filter( i => i.y !== null).length > 2

            for (const [index, item] of dataPoints.entries()) {
                // Ignore the limit dataset
                if (index === 0) {
                    continue
                }

                // Ignore empty datasets
                if (item.y === null) {
                    continue
                }

                data.push({
                    value: roundNumber(item.y, 1, true),
                    unit: null,
                    arrow: null,
                    color: getLineBorderColor(colorsHex.voltage, index, colorsHex.voltage.l1.border),
                })

                // Push a space between every value
                data.push({
                    value: " ",
                    unit: null,
                    arrow: null,
                    color: colorsHex.voltage.unit,
                })
            }

            if (multiplePhases) {
                data.push({
                    value: null,
                    unit: Unit.Voltage,
                    arrow: null,
                    color: colorsHex.voltage.unit,
                })
            } else {
                data.push({
                    value: null,
                    unit: Unit.Voltage,
                    arrow: null,
                    color: colorsHex.voltage.l1.border,
                })
            }

            break
        }

        case 'temperature': {
            data = dataPoints.map(
                (item) => {
                    return {
                        value: roundNumber(item.y, 1),
                        unit: Unit.Celsius,
                        arrow: null,
                        color: colorsHex.temperature.mean.border,
                    }
                }
            ).filter( i => i.color !== undefined )
            break
        }

        case 'humidity': {
            data = dataPoints.map(
                (item) => {
                    return {
                        value: roundNumber(item.y, 1),
                        unit: Unit.Percent,
                        arrow: null,
                        color: colorsHex.humidity.mean.border,
                    }
                }
            ).filter( i => i.color !== undefined )
            break
        }

        case 'water': {
            for (const [_, item] of dataPoints.entries()) {

                // Ignore empty datasets
                if (item.y === null) {
                    continue
                }

                data.push({
                    value: roundNumber(item.y),
                    unit: null,
                    arrow: ArrowIcon.Down,
                    color: colorsHex.water.import.border,
                })

                data.push({
                    value: " ",
                    unit: null,
                    arrow: null,
                    color: colorsHex.water.import.border,
                })

                data.push({
                    value: null,
                    unit: Unit.LiterPerMinute,
                    arrow: null,
                    color: colorsHex.water.import.border,
                })
            }

            break
        }

        case 'battery': {
            const item = dataPoints[0]

            const isExport = item.y < 0

            const colorMap = isExport ? colorsHex.power.export : colorsHex.power.import
            const color = getLineBorderColor(colorMap, 1, colorMap.l1.border)

            data.push({
                value: roundNumber(Math.abs(item.y)),
                unit: null,
                arrow: isExport ? ArrowIcon.Up : ArrowIcon.Down,
                color: color,
            }, {
                value: " ",
                unit: null,
                arrow: null,
                color: colorsHex.power.unit,
            }, {
                value: null,
                unit: Unit.Wattage,
                arrow: null,
                color: color,
            })
            break
        }

        default: {
            // any and all other "power" graphs

            // When multiple phases are to be displayed we need to use a different color for the unit
            const multiplePhases = dataPoints.filter( i => i.y !== null).length > 1

            for (const [index, item] of dataPoints.entries()) {

                // Ignore empty datasets
                if (item.y === null) {
                    continue
                }

                const isExport = item.y < 0 || type === 'solar' || type === 'solar_combined'

                const colorMap = isExport ? colorsHex.power.export : colorsHex.power.import
                const color = getLineBorderColor(colorMap, index + 1, colorMap.l1.border)

                data.push({
                    value: roundNumber(Math.abs(item.y)),
                    unit: null,
                    arrow: isExport ? ArrowIcon.Up : ArrowIcon.Down,
                    color: color,
                }, {
                    value: " ",
                    unit: null,
                    arrow: null,
                    color: colorsHex.power.unit,
                })
            }

            // Display unit as a separate data item

            if (multiplePhases) {
                data.push({
                    value: null,
                    unit: Unit.Wattage,
                    arrow: null,
                    color: colorsHex.power.unit,
                })
            } else {
                const unitColor = data[0].color ?? colorsHex.power.unit

                data.push({
                    value: null,
                    unit: Unit.Wattage,
                    arrow: null,
                    color: unitColor,
                })
            }
        }
    }

    return data
}

export { ArrowIcon, Unit, roundNumber, transformLiveDataPoints }
